import { useEffect, useState } from "react";
import { toast } from "sonner";
import { MessageCircleQuestion, FileText, Film, Presentation, Users2, Layers } from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "../../../components/ui/dialog";
import { Button } from "../../../components/ui/button";
import { Textarea } from "../../../components/ui/textarea";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "../../../components/ui/select";
import AudienceGate from "../files/AudienceGate";
import { useFeedbackEvidenceCatalog, useCreateFeedbackRequest } from "@/lib/feedbackQueries";

const CATEGORIES = [
  { key: "sessions", label: "A Session", icon: Users2, catalogKey: "sessions", targetType: "session" },
  { key: "rehearsals", label: "A Rehearsal", icon: Film, catalogKey: "rehearsals", targetType: "rehearsal" },
  { key: "assignments", label: "An Assignment", icon: FileText, catalogKey: "assignments", targetType: "assignment" },
  { key: "presentation_projects", label: "A Presentation", icon: Presentation, catalogKey: "presentation_projects", targetType: "presentation_project" },
  { key: "files", label: "A File", icon: FileText, catalogKey: "files", targetType: "file_asset" },
  { key: "layers", label: "A Layer", icon: Layers, catalogKey: "layers", targetType: "layer" },
];

/**
 * Master v5 Prompt 12 — "Request Feedback": select/reuse an authorized,
 * ready target, preview the pick, ask a specific question, and (only in
 * a Team Workspace) resolve who else can see that the request exists via
 * AudienceGate. Never lets the requester ask about something they can't
 * already see themselves — the catalog is always pre-scoped server-side
 * to the caller's own work.
 */
export default function RequestFeedbackDialog({ open, onOpenChange, workspaceId, isTeamWorkspace, prefill }) {
  const { data: catalog } = useFeedbackEvidenceCatalog(workspaceId);
  const createRequest = useCreateFeedbackRequest(workspaceId);
  const [categoryKey, setCategoryKey] = useState(null);
  const [targetId, setTargetId] = useState(null);
  const [question, setQuestion] = useState("");
  const [audience, setAudience] = useState(null);

  useEffect(() => {
    if (open && prefill) {
      const cat = CATEGORIES.find((c) => c.targetType === prefill.target_type);
      setCategoryKey(cat?.key || null);
      setTargetId(prefill.target_id);
    } else if (!open) {
      setCategoryKey(null); setTargetId(null); setQuestion(""); setAudience(null);
    }
  }, [open, prefill]);

  const category = CATEGORIES.find((c) => c.key === categoryKey);
  const catalogItems = category ? (category.catalogKey === "layers"
    ? (catalog?.layers || []).map((l) => ({ target_id: l, label: l.replace(/_/g, " ") }))
    : (catalog?.[category.catalogKey] || [])) : [];
  const picked = prefill?.target_id === targetId ? prefill : catalogItems.find((i) => i.target_id === targetId);
  const canSubmit = !!(category && targetId && question.trim() && (!isTeamWorkspace || audience));

  async function submit() {
    try {
      await createRequest.mutateAsync({
        evidence: { target_type: category.targetType, target_id: targetId, label: picked?.label },
        question_text: question.trim(),
        audience_selection: audience,
      });
      toast.success("Feedback request sent to your coach.");
      onOpenChange(false);
    } catch (e) {
      toast.error(e.body?.detail || "Couldn't send that request");
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="request-feedback-dialog" className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><MessageCircleQuestion size={18} className="text-terracotta" /> Request Feedback</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-1.5">
            <label className="text-xs font-medium text-ink-soft">What is this feedback about?</label>
            <Select value={categoryKey || undefined} onValueChange={(v) => { setCategoryKey(v); setTargetId(null); }} disabled={!!prefill}>
              <SelectTrigger data-testid="request-feedback-category-select"><SelectValue placeholder="Choose a category…" /></SelectTrigger>
              <SelectContent>
                {CATEGORIES.map((c) => <SelectItem key={c.key} value={c.key} data-testid={`request-feedback-category-${c.key}`}>{c.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>

          {category && !prefill && (
            <div className="space-y-1.5">
              <label className="text-xs font-medium text-ink-soft">Which one?</label>
              {catalogItems.length === 0 ? (
                <p className="text-xs text-ink-soft italic">Nothing available yet in this category.</p>
              ) : (
                <Select value={targetId || undefined} onValueChange={setTargetId}>
                  <SelectTrigger data-testid="request-feedback-item-select"><SelectValue placeholder="Pick one…" /></SelectTrigger>
                  <SelectContent>
                    {catalogItems.map((i) => <SelectItem key={i.target_id} value={i.target_id}>{i.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              )}
            </div>
          )}

          {picked && (
            <div className="rounded-md bg-ink/5 px-3 py-2 text-sm text-ink" data-testid="request-feedback-preview">
              Requesting feedback on: <span className="font-medium">{picked.label}</span>
            </div>
          )}

          <div className="space-y-1.5">
            <label className="text-xs font-medium text-ink-soft">Your question for the coach</label>
            <Textarea
              value={question} onChange={(e) => setQuestion(e.target.value)} rows={3}
              placeholder="e.g. Did my opening land the way I wanted?" data-testid="request-feedback-question-input"
            />
          </div>

          {isTeamWorkspace && (
            <AudienceGate actorRole="client_member" onConfirm={setAudience} />
          )}
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} data-testid="request-feedback-cancel-btn">Cancel</Button>
          <Button onClick={submit} disabled={!canSubmit || createRequest.isPending} data-testid="request-feedback-submit-btn">
            {createRequest.isPending ? "Sending…" : "Send Request"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
